import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, mailto, CONTACT_EMAIL } from "@/data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[box-shadow,background-color,border-color] duration-500 ${
        scrolled
          ? "border-slate-200/70 bg-white/80 shadow-[0_10px_40px_-20px_rgba(15,23,42,0.25)]"
          : "border-white/40 bg-white/60"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-home-link" aria-label="TecKube home">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 md:flex" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `navlink font-display text-[15px] font-medium transition-colors duration-300 ${
                  isActive ? "active text-ink" : "text-soot hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="btn btn-primary px-6 py-3 text-sm"
          >
            Discuss Your Project
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-ink md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-x-0 top-[76px] bottom-0 z-40 bg-paper/95 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="flex h-full flex-col justify-between px-6 pb-10 pt-8">
              <nav className="flex flex-col gap-2" aria-label="Mobile">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i, duration: 0.35 }}
                  >
                    <Link
                      to={link.to}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                      className="block border-b border-slate-200 py-4 font-display text-3xl font-semibold tracking-tight text-ink"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="space-y-4">
                <Link
                  to="/contact"
                  data-testid="mobile-cta-button"
                  className="btn btn-primary w-full py-4 text-base"
                >
                  Discuss Your Project
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a
                  href={mailto("TecKube enquiry")}
                  data-testid="mobile-email-link"
                  className="flex items-center justify-center gap-2 font-mono text-sm text-soot"
                >
                  <Mail className="h-4 w-4" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
